// Physical content fragments never have timestamps, so they do not occupy
// positions in the message window or its pagination. One query snapshot holds
// all fragments of a message; its revision must agree with the root snapshot.
import { getDoc, getDocs, onSnapshot, query, where } from 'firebase/firestore';

export function hydrateMessageContent(root, fragments) {
  if (root.content_revision == null) {
    if (root.content_fragment_count != null) throw new Error('Message fragment count has no revision');
    return root;
  }
  const revision = root.content_revision;
  const count = root.content_fragment_count;
  if (!Number.isSafeInteger(revision) || revision < 1 || !Number.isSafeInteger(count) || count < 0) {
    throw new Error('Invalid message content revision or fragment count');
  }
  if (!Array.isArray(root.content) || root.content.length) {
    throw new Error('Fragmented message also contains inline content');
  }
  if (!fragments || fragments.length !== count) return null;
  let latestRevision = 0;
  const groups = new Map();
  fragments.forEach((fragment) => {
    if (fragment.type !== 'message_content'
      || typeof fragment.content_group !== 'string'
      || !Number.isSafeInteger(fragment.content_revision) || fragment.content_revision < 1
      || !Number.isSafeInteger(fragment.fragment_index) || fragment.fragment_index < 0
      || !Number.isSafeInteger(fragment.fragment_count) || fragment.fragment_count < 1
      || typeof fragment.content_json !== 'string') {
      throw new Error('Invalid message content fragment');
    }
    latestRevision = Math.max(latestRevision, fragment.content_revision);
    const entries = groups.get(fragment.content_group) || [];
    entries.push(fragment);
    groups.set(fragment.content_group, entries);
  });
  // Either listener can deliver first. Keep the last complete view until both
  // snapshots describe the same transaction, including a delete-only update.
  if (count && latestRevision !== revision) return null;
  const content = [];
  groups.forEach((entries) => {
    entries.sort((left, right) => left.fragment_index - right.fragment_index);
    if (entries.some((entry, index) => entry.fragment_index !== index || entry.fragment_count !== entries.length)) {
      throw new Error('Missing or duplicate message content fragments');
    }
    const parts = JSON.parse(entries.map((entry) => entry.content_json).join(''));
    if (!Array.isArray(parts) || parts.some((part) => !part || typeof part !== 'object' || Array.isArray(part))) {
      throw new Error('Invalid message content parts');
    }
    content.push(...parts);
  });
  content.sort((left, right) => left.cycle - right.cycle || left.ordinal - right.ordinal);
  const identities = new Set();
  content.forEach((part) => {
    if (!Number.isSafeInteger(part.cycle) || part.cycle < 0 || !Number.isSafeInteger(part.ordinal) || part.ordinal < 0) {
      throw new Error('Invalid message part position');
    }
    const identity = `${part.cycle}:${part.ordinal}`;
    if (identities.has(identity)) throw new Error('Duplicate message part position');
    identities.add(identity);
  });
  const { content_revision, content_fragment_count, ...body } = root;
  return { ...body, content };
}

export function subscribeMessageWindow(messagesRef, windowQuery, onReady, onError) {
  const watchers = new Map();
  let roots = null;
  let stopped = false;

  const publish = () => {
    if (stopped || !roots) return;
    try {
      const bodies = new Map();
      for (const snapshot of roots.docs) {
        const body = hydrateMessageContent(snapshot.data(), watchers.get(snapshot.id)?.fragments);
        if (!body) return;
        bodies.set(snapshot.id, body);
      }
      onReady(roots, bodies);
    } catch (error) {
      onError(error);
    }
  };

  const unsubscribe = onSnapshot(windowQuery, (snapshot) => {
    if (stopped) return;
    roots = snapshot;
    const chunked = new Set(snapshot.docs.filter((entry) => entry.data().content_revision != null).map((entry) => entry.id));
    watchers.forEach((watcher, id) => {
      if (!chunked.has(id)) {
        watcher.stop();
        watchers.delete(id);
      }
    });
    chunked.forEach((id) => {
      if (watchers.has(id)) return;
      const watcher = { fragments: null, stop: () => {} };
      watchers.set(id, watcher);
      watcher.stop = onSnapshot(query(messagesRef, where('content_message_id', '==', id)), (fragments) => {
        if (stopped || watchers.get(id) !== watcher) return;
        watcher.fragments = fragments.docs.map((entry) => entry.data());
        publish();
      }, (error) => { if (!stopped) onError(error); });
    });
    publish();
  }, (error) => { if (!stopped) onError(error); });

  return () => {
    stopped = true;
    unsubscribe();
    watchers.forEach((watcher) => watcher.stop());
    watchers.clear();
  };
}

export async function loadMessageContent(snapshot) {
  let current = snapshot;
  for (let attempt = 0; attempt < 5; attempt += 1) {
    if (!current.exists()) return null;
    const root = current.data();
    if (root.content_revision == null) return hydrateMessageContent(root, null);
    const fragments = await getDocs(query(current.ref.parent, where('content_message_id', '==', current.id)));
    const body = hydrateMessageContent(root, fragments.docs.map((entry) => entry.data()));
    if (body) return body;
    current = await getDoc(current.ref);
  }
  throw new Error('Message changed while loading; retry loading its history');
}
